<template>
  <div>
    <CaseReply
      v-if="selectedTicket"
      :ticket="selectedTicket"
      @back="selectedTicket = null"
      @updated="handleTicketUpdated"
      @deleted="handleTicketDeleted"
    />

    <div v-else class="table-container">
      <v-card flat class="pa-4 w-100 ticket-list-card">
        <div
          ref="tableToolbar"
          class="d-flex flex-wrap justify-space-between align-start mb-3"
          style="gap: 12px"
        >
          <div>
            <span class="text-h5">Cases</span>
          
          </div>

          <div class="d-flex flex-wrap align-center" style="gap: 8px">
            <v-text-field
              v-model="searchQuery"
              class="top-input"
              placeholder="Find case..."
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
            />

            <v-select
              v-model="selectedStatus"
              class="status-select"
              :items="statusFilterOptions"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              label="Status"
            />
          </div>
        </div>

        <div ref="tableWrap" class="mt-1">
          <v-data-table
            v-model:page="currentPage"
            v-model:items-per-page="itemsPerPage"
            :headers="headers"
            :items="tableLoading ? [] : filteredTickets"
            class="elevation-0 ticket-table"
            density="comfortable"
            :items-per-page-options="itemsPerPageOptions"
            :height="tableHeight"
            fixed-header
            :loading="tableLoading"
            :no-data-text="tableLoading ? '' : noDataText"
            :row-props="getRowProps"
            @click:row="handleRowClick"
          >
            <template #loading>
              <v-skeleton-loader class="mx-4 my-2" type="table-row@8" />
            </template>

            <template #[`item.caseId`]="{ item }">
              <span class="ticket-id">{{ item.caseId }}</span>
            </template>

            <template #[`item.subject`]="{ item }">
              <div class="subject-wrap">
                <div class="subject-line">{{ item.subject }}</div>
                <div v-if="item.meta" class="text-caption text-medium-emphasis">{{ item.meta }}</div>
              </div>
            </template>

            <template #[`item.status`]="{ item }">
              <span
                class="status-chip"
                :style="chipStyle(statusColorMap[item.status] || '#78909C')"
              >
                <span
                  class="tone-dot"
                  :style="{ background: statusColorMap[item.status] || '#78909C' }"
                />
                {{ item.status }}
              </span>
            </template>

            <template #[`item.severity`]="{ item }">
              <span class="severity-cell">
                <span
                  class="tone-dot"
                  :style="{ background: severityColorMap[item.severity] || '#78909C' }"
                />
                <span :style="{ color: severityColorMap[item.severity] || '#78909C' }">
                  {{ item.severity }}
                </span>
              </span>
            </template>

            <template #[`item.created`]="{ item }">
              <div class="date-cell">
                <v-icon size="16" color="grey">mdi-calendar-blank-outline</v-icon>
                <span>{{ item.created }}</span>
              </div>
            </template>

            <template #[`item.updated`]="{ item }">
              <div class="date-cell">
                <v-icon size="16" color="grey">mdi-clock-outline</v-icon>
                <span>{{ item.updated }}</span>
              </div>
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex align-center justify-end" style="gap: 2px">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  color="primary"
                  @click.stop="openLogsDialog(item)"
                >
                  <v-icon size="20">mdi-chat-processing-outline</v-icon>
                  <v-tooltip activator="parent" location="top">Logs</v-tooltip>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  color="error"
                  @click.stop="openDeleteDialog(item)"
                >
                  <v-icon size="20">mdi-delete-outline</v-icon>
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>

    <ChatbotLogsDialog v-model="showLogsDialog" :ticket="ticketForLogs" />

    <v-dialog v-model="confirmDelete" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Delete case?</v-card-title>
        <v-card-text>
          This will permanently remove case
          <strong>{{ ticketToDelete?.caseId }}</strong>. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmDelete = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleteLoading" @click="deleteTicket">
            Delete case
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { edit_ticket } from "@/graphql/mutations";
import CaseReply from "@/components/RCSSupport/CaseReply.vue";
import ChatbotLogsDialog from "@/components/RCSSupport/Dialogs/ChatbotLogsDialog.vue";
import TicketApi from "@/mixins/RCSSupport/TicketApi";
import TicketDetailApi from "@/mixins/RCSSupport/TicketDetailApi";

const client = generateClient();

export default {
  name: "RCSSupportCases",
  mixins: [TicketApi, TicketDetailApi],
  components: {
    CaseReply,
    ChatbotLogsDialog,
  },
  data() {
    return {
      searchQuery: "",
      selectedStatus: "All",
      selectedTicket: null,
      tableLoading: true,
      detailLoading: false,
      windowHeight: window.innerHeight,
      tableTopOffset: 0,
      layoutObserver: null,
      itemsPerPage: 10,
      currentPage: 1,
      itemsPerPageOptions: [
        { value: 10, title: "10" },
        { value: 25, title: "25" },
        { value: 50, title: "50" },
        { value: 100, title: "100" },
        { value: -1, title: "All" },
      ],
      statusFilterOptions: [
        { title: "All", value: "All" },
        { title: "Awaiting RCS Support", value: "Awaiting RCS Support" },
        { title: "In progress", value: "In progress" },
        { title: "Resolved", value: "Resolved" },
        { title: "Auto-closed", value: "Auto-closed" },
      ],
      headers: [
        { title: "Case ID", key: "caseId", width: "180px", nowrap: true },
        { title: "Subject", key: "subject", minWidth: "280px" },
        { title: "Status", key: "status", width: "180px" },
        { title: "Severity", key: "severity", width: "120px" },
        { title: "Created", key: "created", width: "140px" },
        { title: "Last updated", key: "updated", width: "150px" },
        { title: "", key: "actions", sortable: false, width: "96px", align: "end" },
      ],
      tickets: [],
      confirmDelete: false,
      ticketToDelete: null,
      showLogsDialog: false,
      ticketForLogs: null,
      deleteLoading: false,
      snackbar: {
        show: false,
        message: "",
        color: "error",
      },
    };
  },
  computed: {
    noDataText() {
      return "No cases found.";
    },
    statusColorMap() {
      return {
        "Awaiting RCS Support": "#1E88E5",
        "In progress": "#FB8C00",
        "Awaiting your reply": "#FB8C00",
        Resolved: "#43A047",
        "Auto-closed": "#78909C",
      };
    },
    severityColorMap() {
      return {
        Critical: "#E53935",
        High: "#FB8C00",
        Normal: "#43A047",
        Low: "#78909C",
      };
    },
    tableHeight() {
      const minHeight = 295;
      const viewportFillRatio = 0.91;
      const maxContentHeight = Math.floor(this.windowHeight * viewportFillRatio);
      const footerReserve = 72;

      if (!this.tableTopOffset) {
        return Math.max(minHeight, maxContentHeight - 100 - footerReserve);
      }

      return Math.max(minHeight, maxContentHeight - this.tableTopOffset - footerReserve);
    },
    filteredTickets() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.tickets.filter((ticket) => {
        const statusMatch =
          this.selectedStatus === "All" || ticket.status === this.selectedStatus;
        if (!statusMatch) return false;
        if (!q) return true;
        return [ticket.caseId, ticket.subject, ticket.meta, ticket.status, ticket.severity]
          .join(" ")
          .toLowerCase()
          .includes(q);
      });
    },
  },
  watch: {
    selectedStatus() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    this.measureTableLayout();
    window.addEventListener("resize", this.measureTableLayout);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.measureTableLayout);
    }

    this.layoutObserver = new ResizeObserver(() => {
      this.measureTableLayout();
    });

    this.$nextTick(() => {
      if (this.$refs.tableToolbar instanceof Element) {
        this.layoutObserver.observe(this.$refs.tableToolbar);
      }
      this.measureTableLayout();
    });

    await this.loadTickets();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.measureTableLayout);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.measureTableLayout);
    }
    if (this.layoutObserver) {
      this.layoutObserver.disconnect();
      this.layoutObserver = null;
    }
  },
  methods: {
    showError(message) {
      this.snackbar = {
        show: true,
        message: message || "Something went wrong",
        color: "error",
      };
    },
    async loadTickets() {
      this.tableLoading = true;
      try {
        this.tickets = await this.apiListTickets({ limit: 200 });
      } catch (error) {
        console.error("Error loading tickets:", error);
        this.tickets = [];
        this.showError(error.message || "Failed to load cases");
      } finally {
        this.tableLoading = false;
        this.measureTableLayout();
      }
    },
    async handleRowClick(_event, { item }) {
      this.detailLoading = true;
      try {
        const fullTicket = await this.apiGetTicket(item.ticketId || item.ticket_id);
        this.selectedTicket = fullTicket;
      } catch (error) {
        console.error("Error loading ticket detail:", error);
        this.selectedTicket = item;
        this.showError(error.message || "Failed to load case details");
      } finally {
        this.detailLoading = false;
      }
    },
    handleTicketUpdated(updatedTicket) {
      const index = this.tickets.findIndex(
        (t) => t.ticketId === updatedTicket.ticketId
      );
      if (index !== -1) {
        this.tickets.splice(index, 1, updatedTicket);
      }
      this.selectedTicket = updatedTicket;
    },
    handleTicketDeleted(ticket) {
      this.tickets = this.tickets.filter((t) => t.ticketId !== ticket.ticketId);
      this.selectedTicket = null;
    },
    openLogsDialog(ticket) {
      this.ticketForLogs = ticket;
      this.showLogsDialog = true;
    },
    openDeleteDialog(ticket) {
      this.ticketToDelete = ticket;
      this.confirmDelete = true;
    },
    async deleteTicket() {
      if (!this.ticketToDelete) return;
      this.deleteLoading = true;
      try {
        const response = await client.graphql({
          query: edit_ticket,
          variables: {
            input: {
              ticket_id: this.ticketToDelete.ticketId,
              ticket_status: "DEACTIVE",
            },
          },
        });
        const raw = response.data.edit_ticket;
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (result.status !== "SUCCESS") {
          throw new Error(result.status_message || "Failed to delete case");
        }
        this.tickets = this.tickets.filter(
          (t) => t.ticketId !== this.ticketToDelete.ticketId
        );
        this.confirmDelete = false;
        this.ticketToDelete = null;
      } catch (error) {
        console.error("Failed to delete case:", error);
        this.showError(error.message || "Failed to delete case");
        this.confirmDelete = false;
      } finally {
        this.deleteLoading = false;
      }
    },
    measureTableLayout() {
      this.windowHeight = window.innerHeight;

      this.$nextTick(() => {
        const tableWrap = this.$refs.tableWrap;
        if (tableWrap instanceof Element) {
          this.tableTopOffset = tableWrap.getBoundingClientRect().top;
        }
      });
    },
    chipStyle(hex) {
      return {
        color: hex,
        background: `${hex}1A`,
      };
    },
    getRowProps({ item }) {
      const accent = this.statusColorMap[item.status] || "#78909C";
      return {
        style: {
          "--row-accent": accent,
        },
      };
    },
  },
};
</script>

<style scoped>
.ticket-list-card {
  background: transparent;
}

.top-input {
  width: 240px;
}

.status-select {
  width: 230px;
}

.ticket-table {
  background: transparent !important;
}

.ticket-table :deep(.v-data-table-footer__items-per-page) {
  min-width: 180px;
}

.ticket-table :deep(.v-data-table-footer__items-per-page .v-select) {
  min-width: 96px;
  max-width: 110px;
}

.ticket-table :deep(.v-data-table-footer__items-per-page .v-field) {
  min-width: 96px;
}

.ticket-table :deep(.v-data-table-footer__items-per-page .v-field__input) {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
}

.ticket-table :deep(.v-table__wrapper) {
  overflow: auto;
}

.ticket-table :deep(table) {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

.ticket-table :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 5;
}

.ticket-table :deep(thead th) {
  background: #fff !important;
  color: rgba(0, 0, 0, 0.55) !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-bottom: 1px solid #e8edf2 !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 5 !important;
  box-shadow: 0 1px 0 #e8edf2;
}

.ticket-table :deep(tbody tr) {
  cursor: pointer;
  background: #fff !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.ticket-table :deep(tbody tr:hover) {
  background: #fafbfc !important;
}

.ticket-table :deep(tbody td) {
  border-top: 1px solid #eef1f5 !important;
  border-bottom: 1px solid #eef1f5 !important;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
  background: inherit;
}

/* Row gap without breaking sticky header */
.ticket-table :deep(tbody tr + tr td) {
  border-top-width: 10px !important;
  border-top-color: #f5f7fa !important;
}

.ticket-table :deep(tbody td:first-child) {
  border-left: 4px solid var(--row-accent, #1e88e5) !important;
  border-radius: 12px 0 0 12px;
  padding-left: 14px !important;
}

.ticket-table :deep(tbody td:last-child) {
  border-right: 1px solid #eef1f5 !important;
  border-radius: 0 12px 12px 0;
}

.ticket-id {
  font-weight: 700;
  color: #212121;
  white-space: nowrap;
  display: inline-block;
}

.subject-wrap {
  min-width: 0;
  max-width: 100%;
}

.subject-line {
  font-weight: 600;
  color: #212121;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.tone-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.severity-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.date-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .top-input,
  .status-select {
    width: 100%;
  }
}
</style>
